/**
 * Client-side mirrors of WML game state to Firebase RTDB (replacing server Admin writes).
 * Requires RTDB rules that allow authenticated writes under /{sessionId}/wml.
 * See docs/WML_RTD_CLIENT.md
 */
import { getDatabase, ref, update } from "firebase/database";
import { getFirebaseApp } from "./firebaseClient";

function wmlPath(sessionId) {
  return `/${sessionId}/wml`;
}

async function safeUpdate(sessionId, patch) {
  const app = getFirebaseApp();
  if (!app || !process.env.REACT_APP_FIREBASE_DATABASE_URL || !sessionId) {
    return;
  }
  try {
    const db = getDatabase(app);
    await update(ref(db, wmlPath(sessionId)), patch);
  } catch (e) {
    console.warn("[wmlClientRtdb]", e?.message || e);
  }
}

/**
 * Full snapshot from wmlGetState success body (MySQL truth).
 */
export async function publishSnapshotFromGetState(sessionId, data) {
  if (!data?.success) return;
  const votesSummary =
    data.voteStats != null
      ? {
          submittedCount: data.voteStats.submittedCount,
          expectedCount: data.voteStats.expectedCount,
        }
      : null;
  await safeUpdate(sessionId, {
    phase: data.phase ?? "waiting",
    currentRound: data.currentRound ?? null,
    results: data.results ?? null,
    votesSummary,
    roster: data.roster ?? null,
  });
}

export async function publishNextQuestionFromApi(sessionId, data, options = {}) {
  if (!data?.success) return;
  const expected = Number(data.expectedVotes);
  const expectedCount = Number.isFinite(expected) ? expected : 0;
  const currentRound = {
    roundId: data.roundId,
    promptText: data.promptText,
    options: data.options || [],
  };
  const sec = Number(options.questionTimerSeconds);
  const questionDeadlineMs =
    Number.isFinite(sec) && sec > 0 ? Date.now() + sec * 1000 : null;
  await safeUpdate(sessionId, {
    phase: "question",
    currentRound,
    votesSummary: { submittedCount: 0, expectedCount },
    results: null,
    questionDeadlineMs,
  });
}

export async function publishShowResultFromApi(sessionId, data) {
  if (!data?.success) return;
  const results = {
    roundId: data.roundId,
    tallies: data.tallies,
    winnerPlayerId: data.winnerPlayerId,
    voteCount: data.voteCount,
  };
  await safeUpdate(sessionId, {
    phase: "results",
    results,
    questionDeadlineMs: null,
  });
}

export async function publishVoteSummary(sessionId, { submittedCount, expectedCount }) {
  await safeUpdate(sessionId, {
    votesSummary: { submittedCount, expectedCount },
  });
}

export async function publishShowFinalFromApi(sessionId, data) {
  if (!data?.success) return;
  await safeUpdate(sessionId, {
    phase: "final",
    results: { leaderboard: data.leaderboard || [] },
    questionDeadlineMs: null,
  });
}

export async function publishEndFromApi(sessionId, data) {
  if (!data?.success) return;
  await safeUpdate(sessionId, {
    phase: "ended",
    currentRound: null,
    votesSummary: null,
    results: { leaderboard: data.leaderboard || [] },
    questionDeadlineMs: null,
  });
}
